import { Container, Graphics, Sprite, Text, TextStyle } from "pixi.js";
import App from "../../../../vendor_core/core/src/App";
import { Commands } from "../../../../vendor_core/core/src/commands/CommandEnum";
import CommandManager from "../../../../vendor_core/core/src/commands/CommandManager";
import SoundVars from "../../../../vendor_core/core/src/const/SoundVars";
import BetModel from "../../../../vendor_core/core/src/model/BetModel";
import CurrencyUtils from "../../../../vendor_core/core/src/utils/CurrencyUtils";
import Layout from "../../../../vendor_core/core/src/utils/Layout";
import SoundController from "../../../../vendor_core/core/src/utils/SoundController";
import Configs from "../../../../vendor_core/src/vendorConfigs/Configs";
import BURRulesScrollView, { IRulesStyle } from "./BURRulesScrollView";
import {
    IFeaturesPage,
    IPageIcon,
    IPayoutsPage,
    IRulesPage,
    ISettingsPage
} from "./BURSettingsPages";

/**
 * What the layout of one orientation provides, in PSD pixels (all of them go through Layout.scalePixels, so they scale
 * with the screen): the size of the design, where the header title is, the free area for the content of a page
 * (between the header and the page arrows) and the size of the body text. Every other size of the panel is derived
 * from these, see Metrics.
 */
interface IPanelLayout {
    width: number;
    /** Vertical centre of the page title, in the header of the menu */
    titleY: number;
    region: { x: number; y: number; width: number; height: number };
    /** Size of the body text, the "em" that the sizes and the spaces are relative to */
    text: number;
}

const LANDSCAPE: IPanelLayout = {
    width: 1920,
    titleY: 62,
    region: { x: 399, y: 130, width: 1142, height: 680 },
    text: 36
};

const PORTRAIT: IPanelLayout = {
    width: 1080,
    titleY: 155,
    region: { x: 34, y: 229, width: 1012, height: 1232 },
    text: 44
};

/** How the sizes of the panel relate to each other: to the body text (em) and to the free area or the row */
const RATIO = {
    // text sizes, in em
    title: 1.25,
    itemTitle: 1.17,
    lineHeight: 1.3,
    // spaces, in em
    smallGap: 0.4,
    mediumGap: 0.7,
    largeGap: 0.9,
    // rules text (see IRulesStyle), in em
    rulesLine: 1.33,
    rulesParagraphGap: 0.68,
    rulesScrollbar: 0.22,
    rulesScrollbarGutter: 0.6,
    // the history button: the height in em, the rest relative to the height
    buttonHeight: 2.4,
    buttonWidth: 3.8,
    buttonText: 0.47,
    buttonStroke: 0.045,
    buttonY: 0.55,
    // feature pages: the icon relative to its column (landscape) or row (portrait) and to the free area
    columnIcon: 0.5,
    columnIconMaxOfHeight: 0.35,
    columnText: 0.86,
    rowIcon: 0.7,
    rowIconMaxOfWidth: 0.3,
    // payout tables: how much of the free area the table takes, the text relative to the row, the half of the space
    // between a cluster size and its prize relative to the text, the icon relative to its column
    tableOfHeight: { landscape: 0.95, portrait: 0.85 },
    rowText: 0.5,
    rangeToPrize: 0.4,
    tableOffset: 0.1,
    payoutIcon: { landscape: 0.37, portrait: 0.45 },
    payoutIconMin: 0.17
};

const FONT = "notosans";
const FONT_BOLD = "notosans_bold";
const WHITE = "#FFFFFF";

const px = (value: number) => Layout.scalePixels(value);

interface IBuiltPage {
    view: Container;
    /** Payout amounts, which depend on the bet and are set every time the page is shown */
    prizes: { text: Text; payoutKey: string }[];
    scroll?: BURRulesScrollView;
}

/**
 * The paytable and rules pages of the settings menu. Every page is made of a few sprites and texts that the layout of
 * its PSD scene names (see BURSettingsPages), spaced evenly in the free space of the menu. The pages are built when
 * they are needed (or ahead of time, see build) and kept until the screen size or the orientation changes.
 *
 * The panel is placed in the coordinates of the design (PSD) of the menu, so a page takes the same space as the
 * header and the buttons of the menu that is built from the layout.
 */
export default class BURSettingsPanel extends Container {
    private pages: Map<string, IBuiltPage> = new Map();
    private current?: IBuiltPage;
    private highlight?: Sprite;
    private builtFor = "";

    constructor(private readonly models: ISettingsPage[]) {
        super();
    }

    public get count(): number {
        return this.models.length;
    }

    /** Builds a page so that showing it later is instant (page index from 0) */
    public build(index: number): void {
        this.pageOf(index);
    }

    /** Shows the page with the given index (from 0) */
    public show(index: number): void {
        this.dropStalePages();
        if (this.current) {
            this.removeChild(this.current.view);
        }

        this.current = this.pageOf(index);
        this.current.prizes.forEach(({ text, payoutKey }) => {
            text.text = this.prize(payoutKey);
        });
        this.addChild(this.current.view);
    }

    /**
     * Puts the highlight of the menu button of this page behind it. The rectangle is the one of the button, in the
     * coordinates of the panel.
     */
    public setHighlight(x: number, y: number, width: number, height: number): void {
        if (!this.highlight) {
            const texture = App.assets.getTexture(`${this.models[0].id}~%/selected_info`);
            if (!texture) {
                return;
            }
            this.highlight = new Sprite(texture);
            this.addChildAt(this.highlight, 0);
        }
        this.highlight.position.set(x, y);
        this.highlight.width = width;
        this.highlight.height = height;
    }

    /** Forgets the built pages, e.g. when the screen has been resized */
    public invalidate(): void {
        this.pages.forEach((page) => page.view.destroy({ children: true }));
        this.pages.clear();
        this.current = undefined;
        this.builtFor = "";
    }

    /** Whether the built pages were made for another scale or orientation than the current one */
    public isStale(): boolean {
        return !!this.builtFor && this.builtFor !== this.layoutKey();
    }

    private dropStalePages(): void {
        if (this.isStale()) {
            this.invalidate();
        }
    }

    private layoutKey(): string {
        return `${Layout.isLandscape()}|${px(100)}`;
    }

    private get layout(): IPanelLayout {
        return Layout.isLandscape() ? LANDSCAPE : PORTRAIT;
    }

    private pageOf(index: number): IBuiltPage {
        const model = this.models[index];
        let page = this.pages.get(model.id);
        if (!page) {
            page = this.buildPage(model);
            this.pages.set(model.id, page);
            this.builtFor = this.layoutKey();
        }
        return page;
    }

    private buildPage(model: ISettingsPage): IBuiltPage {
        const page: IBuiltPage = { view: new Container(), prizes: [] };
        page.view.addChild(this.title(model.title));

        switch (model.kind) {
            case "features":
                this.buildFeatures(page, model);
                break;
            case "payouts":
                this.buildPayouts(page, model);
                break;
            case "rules":
                this.buildRules(page, model);
                break;
        }
        return page;
    }

    private title(text: string): Text {
        const title = this.text(text, this.layout.text * RATIO.title, { bold: true, align: "center" });
        title.anchor.set(0.5);
        title.position.set(px(this.layout.width / 2), px(this.layout.titleY));
        return title;
    }

    // --- features: icon, title and description of each item

    private buildFeatures(page: IBuiltPage, model: IFeaturesPage): void {
        const { region, text: em } = this.layout;
        const count = model.items.length;
        const landscape = Layout.isLandscape();
        const titleSize = em * RATIO.itemTitle;
        const smallGap = em * RATIO.smallGap;

        if (landscape) {
            // one column per item: the icon, the title and the description below it, centred. The columns share their
            // top, so the icons are on one line.
            const columnWidth = region.width / count;
            const textWidth = columnWidth * RATIO.columnText;
            const iconBox = Math.min(columnWidth * RATIO.columnIcon, region.height * RATIO.columnIconMaxOfHeight);
            const columns = model.items.map((item, index) => {
                const column = new Container();
                let y = 0;
                const icon = this.icon(item.icon, iconBox);
                if (icon) {
                    icon.position.set(0, px(y + iconBox / 2));
                    column.addChild(icon);
                    y += iconBox + em * RATIO.mediumGap;
                }
                const title = this.text(item.title, titleSize, { bold: true, align: "center", wrap: textWidth });
                title.anchor.set(0.5, 0);
                title.position.set(0, px(y));
                const description = this.text(item.description, em, { align: "center", wrap: textWidth });
                description.anchor.set(0.5, 0);
                description.position.set(0, title.y + title.height + px(smallGap));
                column.addChild(title, description);
                column.x = px(region.x + columnWidth * (index + 0.5));
                page.view.addChild(column);
                return column;
            });

            const tallest = Math.max(0, ...columns.map((column) => column.height));
            columns.forEach((column) => {
                column.y = px(region.y) + (px(region.height) - tallest) / 2;
            });
            return;
        }

        // one row per item: the icon on the left, the title and the description on the right
        const rowHeight = region.height / count;
        const iconBox = Math.min(rowHeight * RATIO.rowIcon, region.width * RATIO.rowIconMaxOfWidth);
        const padding = em * RATIO.largeGap;
        const textX = padding + iconBox + padding;
        const textWidth = region.width - textX - padding;
        model.items.forEach((item, index) => {
            const centerY = px(region.y + rowHeight * (index + 0.5));
            const title = this.text(item.title, titleSize, { bold: true, wrap: textWidth });
            const description = this.text(item.description, em, { wrap: textWidth });
            const textHeight = title.height + px(smallGap) + description.height;
            title.position.set(px(region.x + textX), centerY - textHeight / 2);
            description.position.set(px(region.x + textX), title.y + title.height + px(smallGap));
            page.view.addChild(title, description);
            const icon = this.icon(item.icon, iconBox);
            if (icon) {
                icon.position.set(px(region.x + padding + iconBox / 2), centerY);
                page.view.addChild(icon);
            }
        });
    }

    // --- payouts: two symbols with the prize for each cluster size

    private buildPayouts(page: IBuiltPage, model: IPayoutsPage): void {
        const { region, text: em } = this.layout;
        const landscape = Layout.isLandscape();
        const orientation = landscape ? "landscape" : "portrait";
        const columnWidth = region.width / model.symbols.length;
        const iconBox = columnWidth * RATIO.payoutIcon[orientation];

        // landscape: the icon on the left of the table, portrait: above it
        const tableTop = landscape ? 0 : iconBox + em * RATIO.largeGap;
        const tableX = columnWidth * (0.5 + (landscape ? RATIO.tableOffset : 0));

        model.symbols.forEach((symbol, index) => {
            const column = new Container();
            const rowCount = symbol.rows.length;
            const rowHeight = (region.height * RATIO.tableOfHeight[orientation] - tableTop) / rowCount;
            const tableHeight = rowHeight * rowCount;
            const textSize = rowHeight * RATIO.rowText;
            const halfGap = textSize * RATIO.rangeToPrize;

            const ranges: Text[] = [];
            symbol.rows.forEach((row, rowIndex) => {
                const y = px(tableTop + rowHeight * (rowIndex + 0.5));
                const range = this.text(row.range, textSize, { align: "right" });
                range.anchor.set(1, 0.5);
                range.position.set(px(tableX - halfGap), y);
                const prize = this.text(" ", textSize);
                prize.anchor.set(0, 0.5);
                prize.position.set(px(tableX + halfGap), y);
                column.addChild(range, prize);
                ranges.push(range);
                page.prizes.push({ text: prize, payoutKey: row.payoutKey });
            });

            // in landscape the icon takes the space left of the widest cluster size, so they never touch
            const rangeLeft = px(tableX - halfGap) - Math.max(...ranges.map((range) => range.width));
            const size = landscape
                ? Math.max(columnWidth * RATIO.payoutIconMin, Math.min(iconBox, rangeLeft / px(1) - em * RATIO.largeGap))
                : iconBox;
            const icon = this.icon(symbol.icon, size);
            if (icon) {
                icon.position.set(
                    landscape ? rangeLeft / 2 : px(columnWidth / 2),
                    px(landscape ? tableHeight / 2 : size / 2)
                );
                column.addChild(icon);
            }

            const contentHeight = landscape ? Math.max(tableHeight, size) : tableTop + tableHeight;
            column.position.set(
                px(region.x + columnWidth * index),
                px(region.y) + (px(region.height) - px(contentHeight)) / 2
            );
            page.view.addChild(column);
        });
    }

    private prize(payoutKey: string): string {
        const item = (Configs.settings?.ptMap || []).find((mapItem) => mapItem.key === payoutKey);
        if (!item) {
            return " ";
        }

        const value = (BetModel.totalBet / BetModel.betDivisor) * item.multiplier * BetModel.currencyRate;
        const localization = BetModel.currencyLocalization;
        return localization ? CurrencyUtils.formatCurrency(value, localization) : value.toFixed(2);
    }

    // --- rules: a scrollable list of paragraphs

    private buildRules(page: IBuiltPage, model: IRulesPage): void {
        const { region, text: em } = this.layout;
        // the scroll view starts a bit below the header
        const topPadding = em * RATIO.mediumGap;
        const style: IRulesStyle = {
            fontSize: em,
            lineHeight: em * RATIO.rulesLine,
            paragraphGap: em * RATIO.rulesParagraphGap,
            scrollbarWidth: em * RATIO.rulesScrollbar,
            scrollbarGutter: em * RATIO.rulesScrollbarGutter
        };
        const scroll = new BURRulesScrollView(
            model.paragraphs,
            {
                x: region.x,
                y: region.y + topPadding,
                width: region.width,
                height: region.height - topPadding
            },
            style
        );
        page.view.addChild(scroll);
        page.scroll = scroll;

        if (model.historyLabel) {
            const button = this.historyButton(model.historyLabel);
            // below the text of the page
            button.position.set(px(region.x + region.width / 2), px(region.y + region.height * RATIO.buttonY));
            page.view.addChild(button);
        }
    }

    private historyButton(label: string): Container {
        const height = this.layout.text * RATIO.buttonHeight;
        const width = height * RATIO.buttonWidth;
        const button = new Container();

        const background = new Graphics();
        background.lineStyle(px(height * RATIO.buttonStroke), 0xffffff, 1);
        background.beginFill(0x000000, 0.01);
        background.drawRoundedRect(-px(width) / 2, -px(height) / 2, px(width), px(height), px(height) / 2);
        background.endFill();
        const text = this.text(label, height * RATIO.buttonText, { bold: true, align: "center" });
        text.anchor.set(0.5);
        button.addChild(background, text);

        button.interactive = true;
        button.buttonMode = true;
        button.on("pointerdown", (event: { stopPropagation: () => void }) => {
            event.stopPropagation();
            CommandManager.execute(Commands.REQUEST_HISTORY);
            SoundController.playSound(SoundVars.BUTTON_CLICK);
        });
        return button;
    }

    // --- building blocks

    private text(
        value: string,
        size: number,
        options: { bold?: boolean; align?: "left" | "center" | "right"; wrap?: number } = {}
    ): Text {
        return new Text(
            value,
            new TextStyle({
                fontFamily: options.bold ? FONT_BOLD : FONT,
                fontSize: px(size),
                lineHeight: px(size * RATIO.lineHeight),
                fill: WHITE,
                align: options.align || "left",
                wordWrap: options.wrap !== undefined,
                wordWrapWidth: options.wrap !== undefined ? px(options.wrap) : 0,
                breakWords: true
            })
        );
    }

    /** The icon fitted in a square of the given size (PSD pixels), centred on its position */
    private icon(icon: IPageIcon | undefined, box: number): Sprite | undefined {
        const texture = icon && App.assets.getTexture(icon.textureId);
        if (!icon || !texture) {
            return undefined;
        }

        const fit = Math.min(box / icon.width, box / icon.height);
        const sprite = new Sprite(texture);
        sprite.anchor.set(0.5);
        sprite.width = px(icon.width * fit);
        sprite.height = px(icon.height * fit);
        return sprite;
    }
}
